import { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Select, Space, Typography, message } from "antd";
import { Mail, MapPin, Phone, Plus, Save, Search, Store, Trash2, Users, X } from "lucide-react";
import axiosClient from "../../api/axiosClient";

const { Title, Text } = Typography;

const SERVICES = [
  "Hair Coloring", "Highlights", "Balayage", "Color Correction",
  "Men's Haircut", "Women's Haircut", "Hair Styling", "Blowout & Style",
  "Deep Conditioning", "Beard Trim",
];

interface BoothClient {
  client_id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  favorite_service: string;
  notes: string;
  created_at: string;
}

const emptyForm = { name: "", email: "", phone: "", address: "", favorite_service: SERVICES[0], notes: "" };

export default function AdminBoothClientsPage() {
  const [clients, setClients] = useState<BoothClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<BoothClient | null>(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<BoothClient | null>(null);
  const [form] = Form.useForm();

  function fetchClients() {
    setLoading(true);
    axiosClient
      .get("/booth-clients")
      .then((res) => setClients(res.data ?? []))
      .catch(() => message.error("Failed to load clients"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchClients();
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return clients
      .filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.phone.includes(searchQuery))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, searchQuery]);

  function openAdd() {
    setEditingClient(null);
    form.resetFields();
    form.setFieldsValue(emptyForm);
    setFormOpen(true);
  }

  function openEdit(client: BoothClient) {
    setEditingClient(client);
    form.setFieldsValue(client);
    setFormOpen(true);
  }

  async function handleFormOk() {
    const vals = await form.validateFields();
    try {
      if (editingClient) {
        await axiosClient.put(`/booth-clients/${editingClient.client_id}`, vals);
        message.success("Client updated");
      } else {
        await axiosClient.post("/booth-clients", vals);
        message.success("Client added");
      }
      setFormOpen(false);
      fetchClients();
    } catch {
      message.error(editingClient ? "Failed to update client" : "Failed to add client");
    }
  }

  async function handleDelete(client: BoothClient) {
    Modal.confirm({
      title: "Remove client?",
      content: `Remove ${client.name} from your client list? This cannot be undone.`,
      okType: "danger",
      okText: "Remove",
      onOk: async () => {
        try {
          await axiosClient.delete(`/booth-clients/${client.client_id}`);
          if (selected?.client_id === client.client_id) setSelected(null);
          fetchClients();
        } catch {
          message.error("Failed to remove client");
        }
      },
    });
  }

  async function saveNotes() {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await axiosClient.put(`/booth-clients/${selected.client_id}`, { ...selected, notes: notesDraft });
      setSelected({ ...selected, notes: notesDraft });
      setClients((prev) => prev.map((c) => (c.client_id === selected.client_id ? { ...c, notes: notesDraft } : c)));
      setEditingNotes(false);
      message.success("Notes saved");
    } catch {
      message.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "#f3e8ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Store size={18} color="#9333ea" />
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>My Booth Clients</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>Your personal client list — separate from the salon's main directory</Text>
          </div>
        </div>
        <Button type="primary" icon={<Plus size={15} />} onClick={openAdd}>
          Add New Client
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by name, email, or phone…"
          prefix={<Search size={15} color="#9ca3af" />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: selected ? "0 0 55%" : "1 1 auto", background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <Users size={36} color="#e5e7eb" />
              <p style={{ color: "#9ca3af", marginTop: 8 }}>No clients found. Add your first client.</p>
            </div>
          ) : (
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f0f0f0", textAlign: "left", color: "#9ca3af", fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ padding: "10px 16px" }}>Client</th>
                  <th style={{ padding: "10px 16px" }}>Contact</th>
                  <th style={{ padding: "10px 16px" }}>Preferred</th>
                  <th style={{ padding: "10px 16px", width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.client_id}
                    onClick={() => { setSelected(c); setEditingNotes(false); }}
                    style={{ cursor: "pointer", background: selected?.client_id === c.client_id ? "#eff6ff" : undefined, borderBottom: "1px solid #f5f5f5" }}
                  >
                    <td style={{ padding: "10px 16px", fontWeight: 500 }}>{c.name}</td>
                    <td style={{ padding: "10px 16px", color: "#6b7280" }}>{c.phone || c.email || "—"}</td>
                    <td style={{ padding: "10px 16px" }}>
                      {c.favorite_service && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "#f3e8ff", color: "#9333ea" }}>{c.favorite_service}</span>}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(c); }} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#ef4444" }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {filtered.length > 0 && (
            <div style={{ padding: "8px 16px", fontSize: 12, color: "#9ca3af", textAlign: "center", borderTop: "1px solid #f5f5f5" }}>
              {filtered.length} of {clients.length} clients
            </div>
          )}
        </div>

        {selected && (
          <div style={{ flex: "0 0 43%", background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 16, borderBottom: "1px solid #f5f5f5" }}>
              <div>
                <Text strong>{selected.name}</Text>
              </div>
              <Space>
                <Button size="small" onClick={() => openEdit(selected)}>Edit</Button>
                <Button size="small" type="text" icon={<X size={14} />} onClick={() => setSelected(null)} />
              </Space>
            </div>
            <div style={{ padding: 16 }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}><Phone size={13} color="#9ca3af" />{selected.phone || "—"}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}><Mail size={13} color="#9ca3af" />{selected.email || "—"}</div>
                {selected.address && <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}><MapPin size={13} color="#9ca3af" />{selected.address}</div>}
                {selected.favorite_service && (
                  <div style={{ fontSize: 13 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Preferred: </Text>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "#f3e8ff", color: "#9333ea" }}>{selected.favorite_service}</span>
                  </div>
                )}
              </Space>

              <div style={{ marginTop: 16, border: "1px solid #f0f0f0", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#fafafa" }}>
                  <Text style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", color: "#6b7280" }}>Notes</Text>
                  {!editingNotes && (
                    <Button type="link" size="small" onClick={() => { setNotesDraft(selected.notes); setEditingNotes(true); }}>
                      {selected.notes ? "Edit" : "Add note"}
                    </Button>
                  )}
                </div>
                {editingNotes ? (
                  <div style={{ padding: 12 }}>
                    <Input.TextArea rows={4} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} maxLength={500} showCount />
                    <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                      <Button type="primary" size="small" icon={<Save size={12} />} loading={savingNotes} onClick={saveNotes}>Save</Button>
                      <Button size="small" onClick={() => setEditingNotes(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 12, fontSize: 13, color: selected.notes ? "#374151" : "#9ca3af", minHeight: 40 }}>
                    {selected.notes || "No notes yet."}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        title={editingClient ? "Edit Client" : "New Booth Client"}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={handleFormOk}
        okText={editingClient ? "Save Changes" : "Add Client"}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="Client name" />
          </Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="email" label="Email" rules={[{ type: "email", message: "Invalid email" }]}>
              <Input placeholder="email@example.com" />
            </Form.Item>
            <Form.Item name="phone" label="Phone">
              <Input placeholder="(xxx) xxx-xxxx" />
            </Form.Item>
          </div>
          <Form.Item name="address" label="Address">
            <Input placeholder="Street, City, State ZIP" />
          </Form.Item>
          <Form.Item name="favorite_service" label="Preferred Service">
            <Select options={SERVICES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="Color sensitivities, preferences, special requests…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
